import {ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, forwardRef, Input, QueryList, ViewChildren} from '@angular/core';
import {SplitViewDetails} from '@webmate-web-core/shared/components/split-view/interfaces/split-view.interface';
import {VirtualPackage} from '@webmate-web-core/shared/types/packages/package';
import { JsonPipe, NgClass } from '@angular/common';
import {AvatarModule} from '@webmate-web-core/shared/components/avatar/avatar.module';
import {ButtonModule} from 'primeng/button';
import {FontAwesomeModule} from '@fortawesome/angular-fontawesome';
import {FormsModule} from '@angular/forms';
import {InputTextModule} from 'primeng/inputtext';
import {MenuModule} from 'primeng/menu';
import {ProgressSpinnerModule} from 'primeng/progressspinner';
import {TooltipModule} from 'primeng/tooltip';
import {WmDirectivesModule} from '@webmate-web-core/shared/directives/directives.module';
import {convertBytesToSuitableUnitsString} from '@webmate-web-core/shared/utils/utils';
import {MenuItem} from 'primeng/api';
import {PackageService} from '@webmate-web-core/shared/services/package.service';
import {WmNotificationService} from '@webmate-web-core/shared/components/notification/wm-notification.service';
import {DialogModule} from 'primeng/dialog';
import {SelectModule} from 'primeng/select';
import {ProjectId, VirtualPackageId} from '@webmate-web-core/shared/types/ids';
import {copyToClipboard} from '@webmate-web-core/shared/utils/clipboard';
import {WebmateOptions} from '@webmate-web-core/webmate-options';
import {take} from 'rxjs/operators';
import {AssetAndBlobService} from '@webmate-web-core/shared/services/assetAndBlob.service';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {UploadPackagesPermission} from '@webmate-web-core/shared/constants/permission.constants';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {AppUploadToolComponent, AppUploadToolConfig} from '@webmate-web-core/shared/tools/app-upload/app-upload-tool.component';
import {ModalToolContainerService} from '@webmate-web-core/shared/tools/modaltoolcontainer/modaltoolcontainer.service';
import {WmCalendarFormatPipe} from '@webmate-web-core/shared/utils/pipes/wm-calendar-format';
import {WmDateFormatPipe} from '@webmate-web-core/shared/utils/pipes/wm-date-format.pipe';
import {faInfoCircle} from '@fortawesome/pro-light-svg-icons';

export interface AppVariantCallback {
    regularActionName: string;
    regularCallback: (p: VirtualPackage) => void;
    instrumentedActionName: string;
    instrumentedCallback: (p: VirtualPackage) => void;
}

@Component({
    selector: 'wm-app-split-view-details',
    templateUrl: './app-split-view-details.component.html',
    styleUrls: ['./app-split-view-details.component.less'],
    imports: [AvatarModule, ButtonModule, FontAwesomeModule, FormsModule, InputTextModule, MenuModule, ProgressSpinnerModule, TooltipModule, WmDirectivesModule, DialogModule, SelectModule, NgClass, WmCalendarFormatPipe, WmDateFormatPipe],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [{provide: SplitViewDetails, useExisting: forwardRef(() => AppSplitViewDetailsComponent)}]
})
export class AppSplitViewDetailsComponent extends SplitViewDetails<VirtualPackage> {

    protected readonly convertBytesToSuitableUnitsString = convertBytesToSuitableUnitsString;
    protected readonly copyToClipboard = copyToClipboard;

    @ViewChildren('nameInput') nameInput: QueryList<ElementRef>;

    @Input() variantCallbacks: AppVariantCallback;

    @Input() interactive: boolean;

    @Input() projectId: ProjectId;

    private _entity: VirtualPackage;

    logoSrc?: string = undefined;
    platformLogoSrc?: string = undefined;

    modifiedName?: string;
    modifyingName: boolean = false;
    nameModificationInProgress: boolean = false;

    userMayUploadPackages: boolean = false;
    outdatedInstrumentation: boolean = false;

    set entity(e: VirtualPackage) {
        if (e) {
            const previousEntity = this._entity;
            const entityChanged = this._entity?.id !== e.id;
            if (entityChanged) {
                this.clearModifyName();
            } else if (previousEntity?.name !== e.name && this.modifyingName) {
                if (!this.modifiedName || this.modifiedName === previousEntity.name) {
                    this.modifiedName = e.name;
                } else if (this.modifiedName !== e.name) {
                    this.clearModifyName();
                }
            }
            this._entity = e;
            this.logoSrc = this.blobService.obtainUrlToBlobData(e.origMetaData['iconBlobId']);
            this.platformLogoResolver.getLogoForPlatform(e.platform).pipe(take(1)).subscribe(logoSrc => {
                this.platformLogoSrc = logoSrc;
                this.cdRef.markForCheck();
            });

            this.outdatedInstrumentation =
                e.instrumenterVersion !== undefined &&
                (e.platform.platformType === 'android' && e.instrumenterVersion < 2) ||
                (e.platform.platformType === 'ios' && e.instrumenterVersion < 7);

            this.cdRef.markForCheck();
        }
    }

    get entity() {
        return this._entity;
    }

    webhookDialogVisible = false;

    releaseChooserVisible = false;
    releases?: Array<{label: string, value: string}>;
    chosenRelease: string;

    packageSourceUpdating = false;

    packageSourceMenuItems: MenuItem[] = [{
        label: 'Disconnect',
        icon: 'fa-trash',
    }, {
        label: 'Edit',
        icon: 'fa-edit',
        disabled: true,
    }, {
        label: 'Generate Webhook',
        icon: 'fa-link',
        command: (event: any) => {
            this.webhookDialogVisible = true;
        }
    }, {
        label: 'Choose specific release',
        icon: 'fa-thumbtack',
        command: (event: any) => {
            this.releaseChooserVisible = true;
            this.releases = undefined;
            this.packageService.getReleasesFromPackageSourceForPackage(this.entity.id).subscribe(releases => {
                this.releases = releases.map(r => ({
                    label: `${r.short_version} (${r.version})`,
                    value: r.id
                }));
            }, error => {
                this.releaseChooserVisible = false;
                this.notificationService.toastErrorNotification('Unable to retrieve release list: ' + error);
            });
        }
    }];

    constructor(private cdRef: ChangeDetectorRef, private packageService: PackageService,
                private notificationService: WmNotificationService, private webmateOptions: WebmateOptions,
                private blobService: AssetAndBlobService, private platformLogoResolver: LogoResolverService,
                private userService: UserService, private toolService: ModalToolContainerService) {
        super();
    }

    ngOnInit() {
        this.userService.getPermissionsForCurrentUserInProject(this.projectId).subscribe(permissions => {
            this.userMayUploadPackages = permissions.indexOf(UploadPackagesPermission) > -1;
        });
    }

    updatePackageFromPackageSource(packageId: VirtualPackageId, release?: string) {
        this.packageSourceUpdating = true;
        this.packageService.updatePackageFromPackageSource(packageId, release, this.projectId).subscribe(res => {
            this.packageSourceUpdating = false;
            this.releaseChooserVisible = false;
        });
    }

    makeWebhookUrl(webhookId: string): string {
        return this.webmateOptions.baseApiUri + '/webhook/packages/' + webhookId;
    }

    nameClicked(event: any) {
        this.modifiedName = this.modifiedName || this.entity.name || '';
        this.modifyingName = true;
        setTimeout(() => {
            this.nameInput.first.nativeElement.focus();
        })
        event.stopPropagation();
    }

    saveNameModification() {
        if (!this.modifiedName || this.modifiedName === this.entity.name) {
            return;
        }

        this.nameModificationInProgress = true;
        this.packageService.updatePackageMetaInfo(this.entity.id, this.modifiedName, this.projectId).pipe(take(1)).subscribe(() => {
            this.clearModifyName();
        }, error => {
            this.notificationService.toastErrorNotification('Could not set package name: ' + error.statusText);
            this.nameModificationInProgress = false;
        });
    }

    clearModifyName() {
        this.modifiedName = undefined;
        this.modifyingName = false;
        this.nameModificationInProgress = false;
    }

    openReuploadDialog(event: any) {
        const config = new AppUploadToolConfig(this.projectId, 'upload-package', this.entity);
        this.toolService.openTool(AppUploadToolComponent, config);
    }

    protected readonly faInfoCircle = faInfoCircle;
}
